import Button from '@components/Button/Button';
import Typography from '@mui/material/Typography';
import { StyledNewsletter } from './Newsletter.styled';
import Stack from '@mui/material/Stack';
import Input from '@components/Input/Input';
import Box from '@mui/material/Box';
import Checkbox from '@components/Checkbox/Checkbox';
import { Theme, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import LoadingSpinner from '@components/LoadingSpinner/LoadingSpinner';

const Newsletter = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    email: string | null;
    api: string | null;
    tosChecked: string | null;
  }>({
    email: null,
    api: null,
    tosChecked: null,
  });
  const [email, setEmail] = useState('');
  const [tosChecked, setTosChecked] = useState(false);

  const validate = () => {
    const regExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email || !email.match(regExp)) {
      return {
        email: true,
      };
    }

    if (!tosChecked) {
      return {
        tosChecked: true,
      };
    }

    return {
      email: false,
      tosChecked: false,
    };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validate().email)
      return setErrors({
        email: 'There is an error in the email address you entered.',
        api: null,
        tosChecked: null,
      });

    if (validate().tosChecked) {
      return setErrors({
        tosChecked: 'Please read and accept our Terms of Service.',
        api: null,
        email: null,
      });
    }

    setLoading(true);

    setTimeout(async () => {
      try {
        const response = await fetch('/api/subscribe', {
          body: JSON.stringify({ email }),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        });

        if (response.status === 500) {
          setErrors({
            email: null,
            tosChecked: null,
            api: 'Something went wrong. Please try again later.',
          });
        } else {
          setErrors({ email: null, api: null, tosChecked: null });
          setSuccess(
            'Thanks for signing up. We will keep you up to date with our project.'
          );
        }
      } catch (error: any) {
        if (error?.response) {
          const apiError = error?.response?.data.error;
          setErrors({
            email: null,
            tosChecked: null,
            api:
              apiError && apiError === 'email-exists'
                ? "Looks like you've already subscribed, try a different email address."
                : 'Something went wrong. Please try again later.',
          });
          setSuccess(null);
        }
      }

      setEmail('');
      setTosChecked(false);
      setLoading(false);
    }, 1000);
  };

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  return (
    <StyledNewsletter>
      <Typography variant="h3" color="text.primary" mb={1} textAlign="center">
        Join community for Beta access
      </Typography>
      <Typography
        variant="body1"
        color="text.primary"
        component="p"
        mb={4}
        textAlign="center"
      >
        Sign up to our Newsletter and keep up to date with all things walnuts.farm!
      </Typography>
      <form onSubmit={handleSubmit}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Input
            sx={{ width: '100%' }}
            disableUnderline
            placeholder="e-mail address"
            value={email}
            type="email"
            name="email"
            onChange={handleChange}
          />
          <Box minWidth="170px">
            <Box minWidth="170px">
              <Button
                variant="contained"
                color="primary"
                type="submit"
                fullWidth={isMobile}
                loading={loading}
                disabled={loading}
                loadingIndicator={<LoadingSpinner />}
              >
                Subscribe Now
              </Button>
            </Box>
          </Box>
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          onClick={() => setTosChecked(!tosChecked)}
          sx={{
            cursor: 'pointer',
          }}
          mt={4}
        >
          <Checkbox checked={tosChecked} />
          <Typography variant="body1" color="text.primary">
            I agree to the processing of personal data by WalNuts for marketing
            purposes and accept Terms of Service.
          </Typography>
        </Stack>
        {(errors.email || errors.api || errors.tosChecked) && (
          <Typography variant="body2" color="error.light" mt={1}>
            {errors.email || errors.api || errors.tosChecked}
          </Typography>
        )}
        {success && (
          <Typography variant="body2" color="primary" mt={1}>
            {success}
          </Typography>
        )}
      </form>
    </StyledNewsletter>
  );
};

export default Newsletter;
